import { Controller } from "@hotwired/stimulus"

// Copy a press clip to other DPKs (PRT_Clip_Move_And_Copy_Plan.md, D2, D3,
// W4). ONE sheet per Press Clips page. Opening it (a card's More menu, or
// the after-create banner) fetches that clip's checklist from the server
// (copy_options): eligible DPKs A to Z, the ones already holding the link
// checked, grey and disabled. This controller only searches the rows,
// keeps the "4 selected: A, B, C, D" line (which counts every pick, shown
// or filtered out), and labels the button "Copy to N DPKs", disabled as
// "Pick a DPK" at none. No select-all, on purpose.
export default class extends Controller {
  static targets = ["modal", "sheet", "search", "row", "box", "summary", "submit", "empty"]

  connect() {
    this.onKeydown = (event) => {
      if (event.key === "Escape" && !this.modalTarget.classList.contains("hidden")) this.close()
    }
    document.addEventListener("keydown", this.onKeydown)
  }

  disconnect() {
    document.removeEventListener("keydown", this.onKeydown)
  }

  async open(event) {
    event.preventDefault()
    this.opener = event.currentTarget
    this.sheetTarget.replaceChildren()
    this.modalTarget.classList.remove("hidden")
    document.body.style.overflow = "hidden"
    try {
      const response = await fetch(event.params.url, {
        headers: { Accept: "text/html", "X-Requested-With": "XMLHttpRequest" },
        credentials: "same-origin"
      })
      // A gate that redirects (signed out, an intern) is not the list.
      if (!response.ok || response.redirected) throw new Error(`copy options ${response.status}`)
      this.sheetTarget.innerHTML = await response.text()
      this.refresh()
      if (this.hasSearchTarget) this.searchTarget.focus()
    } catch (error) {
      console.warn(error)
      this.showLoadError()
    }
  }

  filter() {
    const query = this.searchTarget.value.trim().toLowerCase()
    let shown = 0
    this.rowTargets.forEach((row) => {
      const hit = query === "" || row.dataset.search.includes(query)
      row.hidden = !hit
      if (hit) shown++
    })
    if (this.hasEmptyTarget) this.emptyTarget.hidden = shown > 0
  }

  toggle() {
    this.refresh()
  }

  // Already-holding rows are checked but disabled: they are not picks.
  refresh() {
    const names = this.boxTargets.filter((box) => box.checked && !box.disabled).map((box) => box.dataset.name)
    if (this.hasSummaryTarget) {
      this.summaryTarget.hidden = names.length === 0
      this.summaryTarget.textContent = names.length ? `${names.length} selected: ${names.join(", ")}` : ""
    }
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = names.length === 0
      this.submitTarget.textContent = names.length === 0
        ? "Pick a DPK"
        : `Copy to ${names.length} ${names.length === 1 ? "DPK" : "DPKs"}`
    }
  }

  // One submit: the page is leaving (turbo:false form), a second click
  // would only post the same copy again.
  submitting() {
    if (this.hasSubmitTarget) setTimeout(() => { this.submitTarget.disabled = true }, 0)
  }

  close() {
    this.modalTarget.classList.add("hidden")
    document.body.style.overflow = ""
    this.sheetTarget.replaceChildren()
    const summary = this.opener?.closest("details")?.querySelector("summary")
    ;(summary || this.opener)?.focus()
  }

  showLoadError() {
    const box = document.createElement("div")
    box.className = "p-5 space-y-4"
    const message = document.createElement("p")
    message.className = "text-sm text-[#2D2D2D]"
    message.setAttribute("role", "alert")
    message.textContent = "Couldn't load the list of DPKs. Close this and try again."
    const close = document.createElement("button")
    close.type = "button"
    close.className = "mc-btn-secondary text-sm"
    close.textContent = "Close"
    close.dataset.action = "clip-copy#close"
    box.append(message, close)
    this.sheetTarget.replaceChildren(box)
  }
}
